/**
 * FreezeSystem - Handles freezing and unfreezing of physics bodies
 * Core mechanic: When piece lands, freeze all placed pieces so player can adjust
 */

import Phaser from 'phaser';

/**
 * Stored velocity data for restoration after freeze
 */
interface VelocityData {
  x: number;
  y: number;
  angular: number;
}

/**
 * System for managing the freeze mechanic
 */
export class FreezeSystem {
  private scene: Phaser.Scene;
  private placedBodies: MatterJS.BodyType[] = [];
  private frozenVelocities: Map<number, VelocityData> = new Map();
  private isFrozen: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Add a body to the list of placed bodies
   */
  addPlacedBody(body: MatterJS.BodyType): void {
    if (!this.placedBodies.includes(body)) {
      this.placedBodies.push(body);
    }
  }

  /**
   * Remove a body from the placed bodies list
   */
  removePlacedBody(body: MatterJS.BodyType): void {
    const index = this.placedBodies.indexOf(body);
    if (index > -1) {
      this.placedBodies.splice(index, 1);
    }
  }

  /**
   * Freeze all placed bodies and stop the active body
   */
  freeze(activeBody: MatterJS.BodyType): void {
    if (this.isFrozen) return;

    this.isFrozen = true;

    // Store velocities before freezing (for potential restoration)
    this.placedBodies.forEach(body => {
      this.frozenVelocities.set(body.id, {
        x: body.velocity.x,
        y: body.velocity.y,
        angular: body.angularVelocity,
      });
    });

    // Make all placed bodies static
    this.placedBodies.forEach(body => {
      this.scene.matter.body.setStatic(body, true);
    });

    // Stop the active body
    this.scene.matter.body.setVelocity(activeBody, { x: 0, y: 0 });
    this.scene.matter.body.setAngularVelocity(activeBody, 0);

    // Make active body static during freeze (will be slidable in Sprint 3)
    this.scene.matter.body.setStatic(activeBody, true);
  }

  /**
   * Unfreeze all bodies and restore physics
   */
  unfreeze(activeBody: MatterJS.BodyType): void {
    if (!this.isFrozen) return;

    this.isFrozen = false;

    // Restore all placed bodies to dynamic
    this.placedBodies.forEach(body => {
      this.scene.matter.body.setStatic(body, false);
    });

    // Add active body to placed bodies
    this.addPlacedBody(activeBody);

    // Make active body dynamic again
    this.scene.matter.body.setStatic(activeBody, false);

    // Clear stored velocities
    this.frozenVelocities.clear();
  }

  /**
   * Get all placed bodies
   */
  getPlacedBodies(): MatterJS.BodyType[] {
    return [...this.placedBodies];
  }

  /**
   * Check if a body is in the placed bodies list
   */
  isPlacedBody(body: MatterJS.BodyType): boolean {
    return this.placedBodies.includes(body);
  }

  /**
   * Check if system is currently frozen
   */
  getIsFrozen(): boolean {
    return this.isFrozen;
  }

  /**
   * Get the number of placed bodies
   */
  getPlacedCount(): number {
    return this.placedBodies.length;
  }

  /**
   * Clear all placed bodies (for scene reset)
   */
  clear(): void {
    this.placedBodies = [];
    this.frozenVelocities.clear();
    this.isFrozen = false;
  }
}
